import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import type { IncomeStatementRecord } from "../hooks/useCompanyIncomeStatement";
import type { AnalystEstimate } from "../hooks/useAnalystEstimates";

dayjs.extend(quarterOfYear);

export type EstimateMetric = "revenue" | "eps";
export type EstimatePeriod = "annual" | "quarter";
export type EstimateLevel = "low" | "average" | "high";

export interface EstimateRange {
  date: string;
  period: string;
  low: number | null;
  average: number | null;
  high: number | null;
  analysts: number | null;
}

const finiteValue = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const hasForecast = (range: EstimateRange) =>
  range.low !== null || range.average !== null || range.high !== null;

export const getAvailableForecastHorizons = (
  ranges: EstimateRange[],
  cutoff: string,
  period: EstimatePeriod,
): number[] => {
  const maxHorizon = period === "annual" ? 5 : 8;
  const monthsPerPeriod = period === "annual" ? 12 : 3;
  const dateUnit = period === "annual" ? "year" : "quarter";
  return Array.from({ length: maxHorizon }, (_, index) => index + 1).filter(
    (horizon) => {
      const start = dayjs(cutoff)
        .add((horizon - 1) * monthsPerPeriod, "month")
        .endOf(dateUnit);
      const end = dayjs(cutoff)
        .add(horizon * monthsPerPeriod, "month")
        .endOf(dateUnit);
      return ranges.some(
        (range) =>
          hasForecast(range) &&
          dayjs(range.date).isAfter(start, "day") &&
          !dayjs(range.date).isAfter(end, "day"),
      );
    },
  );
};

export const getForecastWindow = (
  ranges: EstimateRange[],
  cutoff: string,
  horizon: number,
  period: EstimatePeriod,
): EstimateRange[] => {
  const end = dayjs(cutoff)
    .add(horizon * (period === "annual" ? 12 : 3), "month")
    .endOf(period === "annual" ? "year" : "quarter");
  const forecastWindow = ranges.filter(
    (range) =>
      dayjs(range.date).isAfter(cutoff, "day") &&
      !dayjs(range.date).isAfter(end, "day"),
  );
  // Retain gaps inside the forecast, without extending the chart past coverage.
  const lastCovered = forecastWindow.reduce(
    (last, range, index) => (hasForecast(range) ? index : last),
    -1,
  );
  return forecastWindow.slice(0, lastCovered + 1);
};

export const getEstimateRanges = (
  estimates: AnalystEstimate[],
  metric: EstimateMetric,
  period: EstimatePeriod,
): EstimateRange[] =>
  estimates
    .filter(
      (estimate) => Boolean(estimate.date) && dayjs(estimate.date).isValid(),
    )
    .map((estimate) => {
      const date = dayjs(estimate.date);
      const analysts = finiteValue(
        metric === "revenue"
          ? estimate.numAnalystsRevenue
          : estimate.numAnalystsEps,
      );
      const low = finiteValue(estimate[`${metric}Low`]);
      const average = finiteValue(estimate[`${metric}Avg`]);
      const high = finiteValue(estimate[`${metric}High`]);
      // All-zero placeholders without analyst coverage are not forecasts.
      const noCoverage =
        analysts === 0 ||
        (low === 0 &&
          average === 0 &&
          high === 0 &&
          !(analysts !== null && analysts > 0));
      return {
        date: estimate.date,
        period:
          period === "annual"
            ? `FY ${date.year()}`
            : `Q${date.quarter()} ${date.year()}`,
        low: noCoverage ? null : low,
        average: noCoverage ? null : average,
        high: noCoverage ? null : high,
        analysts: analysts !== null && analysts >= 0 ? analysts : null,
      };
    })
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

export interface ActualResult {
  date: string;
  period: string;
  value: number | null;
}

export const getActualResults = (
  income: IncomeStatementRecord[],
  metric: EstimateMetric,
  period: EstimatePeriod,
): ActualResult[] =>
  income
    .filter((record) => Boolean(record.date) && dayjs(record.date).isValid())
    .map((record) => ({
      date: record.date,
      period:
        period === "annual"
          ? `FY ${dayjs(record.date).year()}`
          : `Q${dayjs(record.date).quarter()} ${dayjs(record.date).year()}`,
      value: finiteValue(record[`${metric}Actual`]),
    }))
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
    .slice(period === "annual" ? -5 : -20);

export const getEstimateSeries = (
  actuals: ActualResult[],
  ranges: EstimateRange[],
) => {
  const anchor = actuals[actuals.length - 1];
  const periods = [...actuals, ...ranges];
  const actualByPeriod = new Map(
    actuals.map((result) => [result.period, result.value]),
  );
  const estimatesByPeriod = new Map(
    ranges.map((range) => [range.period, range]),
  );
  return [
    {
      id: "actual" as const,
      data: periods.map(({ period }) => ({
        x: period,
        y: actualByPeriod.get(period) ?? null,
      })),
    },
    ...(["low", "average", "high"] as const).map((level) => ({
      id: level,
      data: periods.map(({ period }) => ({
        x: period,
        // Each forecast starts at the latest reported result. Nulls remain gaps.
        y:
          period === anchor?.period
            ? anchor.value
            : (estimatesByPeriod.get(period)?.[level] ?? null),
      })),
    })),
  ];
};
